// For future use
// see file history for removed unused functions
// like getHostFromUrl & shouldRedirectToAEM
/* External */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import templateL from "lodash/template";
import isEmptyL from "lodash/isEmpty";

/* Other */
import { DEFAULT_VALUES, USER_TYPES, isBrazil } from "../utils";
import { getURLWithParams } from "../utils/urls/index";

export const MI_DIRECTV = {
  CL: {
    forgotPassword: "https://www.directv.cl/midirectv/contrasena-olvidada",
    selfCare: "https://www.directv.cl/midirectv/ingresar"
  },
  GENERIC: {
    forgotPassword: "https://www.directv.com.{country}/midirectv/contrasena-olvidada",
    selfCare: "https://www.directv.com.{country}/midirectv/ingresar"
  }
};

export const SELF_CARE = {
  main: "/selfcare/",
  roku: "https://my.roku.com/"
};

export const FAQ_URL = {
  url: "/faq"
};

const FAQ = {
  SSLA: "https://www.directvgo.com/{country}/faq/servicio-al-cliente/servicio-al-cliente/contactar-servicio-al-cliente.html",
  BR: "https://www.directvgo.com/br/faq/atendimento-ao-cliente/atendimento-ao-cliente/contatar-o-atendimento-ao-cliente.html"
};

/**
 * @method useGetFaqURL
 * @description Hook that returns the faq url for a given country
 */
export const useGetFaqURL = () => {
  const userCountry = useSelector((state) => state.global?.userCountry) || DEFAULT_VALUES.COUNTRY;
  const [faqURL, setFaqURL] = useState("");

  useEffect(() => {
    setFaqURL(getGetFaqURL(userCountry.toLowerCase(), isBrazil));
  }, [faqURL, userCountry]);

  return faqURL;
};

/**
 * @method getGetFaqURL
 * @description Returns the faq url for a given country
 * @param {string} country
 * @param {boolean} isBrazil
 * @returns {string} url
 */
export const getGetFaqURL = (country, isBrazil) => {
  return isBrazil ? getURLWithParams(FAQ.BR) : getURLWithParams(FAQ.SSLA, { country });
};

/**
 * @method useGetSelfCareURL
 * @description Hook that returns the self care url for a given user
 */
export const useGetSelfCareURL = () => {
  const user = useSelector(({ user = {} }) => user || {});
  const [selfCareURL, setSelfCareURL] = useState("");

  useEffect(() => {
    setSelfCareURL(getSelfCareURL(user));
  }, [user]);

  return selfCareURL;
};

/**
 * @method getSelfCareURL
 * @description Returns the self care url for a given user
 * @param {object} user
 * @returns {string} url
 */
export const getSelfCareURL = (user) => {
  const { account: { country = DEFAULT_VALUES.COUNTRY, isTVE = false, userType = "", msoProvider = "" } = {} } = user;

  if (msoProvider === "roku") return "#";

  const shouldRedirectToExternalSelfCare = isTVE || userType.toLowerCase() === USER_TYPES.LATAM_OTT.toLowerCase();

  return shouldRedirectToExternalSelfCare
    ? buildExternalSelfCareURL(country.toLowerCase())
    : getURLWithParams(SELF_CARE.main);
};

/**
 * @method getPartnerConfig
 * @description Returns the current partner config from the partners config array
 * @param {string} msoProvider
 * @param {array} partnersConfig
 * @returns {object} config
 */
export const getPartnerConfig = (msoProvider = "", partnersConfig) => {
  return partnersConfig.filter((partner) => partner.name.toLowerCase() === msoProvider.toLowerCase())[0] || {};
};

/**
 * @method getPartnerSelfCareURL
 * @description Returns an external self care url based on msoProvider
 * @param {string} msoProvider
 * @param {array} partnersConfig
 * @returns {string} url
 */
export const getPartnerSelfCareURL = (msoProvider, partnersConfig) => {
  const partnerConfig = getPartnerConfig(msoProvider, partnersConfig);
  const isPartnerConfigEmpty = isEmptyL(partnerConfig);
  isPartnerConfigEmpty &&
    console.error(`getPartnerSelfCareURL - AEM Partners Config is empty for ${msoProvider} user provider`);
  return isPartnerConfigEmpty ? "" : getURLWithParams(partnerConfig.selfcareUrl);
};

/**
 * @method buildExternalSelfCareURL
 * @description Returns an external self care url based on country
 * @param {string} country
 * @returns {string} url
 */
export const buildExternalSelfCareURL = (country) => {
  switch (country.toUpperCase()) {
    case "CL":
      return getURLWithParams(MI_DIRECTV.CL.selfCare);
    default:
      return getURLWithParams(MI_DIRECTV.GENERIC.selfCare, { country });
  }
};

/**
 * Method for building an external url (AEM) with redirect
 */
export function externalRedirect(
  url,
  country = DEFAULT_VALUES.COUNTRY,
  language = DEFAULT_VALUES.LANGUAGE,
  openOtherTab = true
) {
  if (url) {
    const urlToRedirect = templateL(url)({
      country: country.toLowerCase(),
      language
    });
    openOtherTab ? window.open(urlToRedirect, "_blank") : (window.location = urlToRedirect);
  }
}

/**
 * Method for building an external url (AEM)
 */

export const getExternalRedirectURL = (url, country = DEFAULT_VALUES.COUNTRY, language = DEFAULT_VALUES.LANGUAGE) => {
  return templateL(url)({
    country: country.toLowerCase(),
    language
  });
};
